<template>
  <div class="medium-12 columns">
    <div class="template-preview">
      <p
        v-if="processedHeaderString && showHeader"
        class="template-preview__header"
      >
        {{ processedHeaderString }}
      </p>
      <textarea
        v-model="processedString"
        rows="4"
        readonly
        class="template-input"
        :class="{ 'template-input--error': hasInvalidValues }"
      />
      <div
        class="size-indicator-container"
        :class="{ 'size-indicator-container--error': isMessageTooLong }"
      >
        <span> {{ messageLength }}/{{ maxLength }} </span>
      </div>
      <div
        v-if="templateButtons.length && showButtons"
        class="template-preview__buttons"
      >
        <span
          v-for="(button, index) in templateButtons"
          :key="`btn-preview-${index}`"
          class="template-preview__button"
        >
          {{ button.text }}
        </span>
      </div>
    </div>
    <div
      v-if="hasVariables || isMessageTooLong"
      class="template__variables-container"
    >
      <p v-if="hasVariables" class="variables-label">
        {{ $t('MESSENGER_TEMPLATES.PARSER.VARIABLES_LABEL') }}
      </p>

      <template v-if="headerVars.length && showHeader">
        <p class="component-label">
          {{ $t('MESSENGER_TEMPLATES.PARSER.HEADER_LABEL') }}
        </p>
        <div
          v-for="(item, index) in headerVars"
          :key="`header-${index}`"
          class="template__variable-item"
        >
          <span class="variable-label">
            {{ item.raw }}
          </span>
          <woot-input
            v-model="item.value"
            type="text"
            class="variable-input"
            :styles="{ marginBottom: 0 }"
          />
        </div>
      </template>

      <template v-if="bodyVars.length">
        <p class="component-label">
          {{ $t('MESSENGER_TEMPLATES.PARSER.BODY_LABEL') }}
        </p>
        <div
          v-for="(item, index) in bodyVars"
          :key="`body-${index}`"
          class="template__variable-item"
        >
          <span class="variable-label">
            {{ item.raw }}
          </span>
          <woot-input
            v-model="item.value"
            type="text"
            class="variable-input"
            :styles="{ marginBottom: 0 }"
          />
        </div>
      </template>

      <template v-if="buttonVars.length && showButtons">
        <p class="component-label">
          {{ $t('MESSENGER_TEMPLATES.PARSER.BUTTONS_LABEL') }}
        </p>
        <div
          v-for="(item, index) in buttonVars"
          :key="`button-${index}`"
          class="template__variable-item"
        >
          <span class="variable-label">
            {{ item.raw }}
          </span>
          <woot-input
            v-model="item.value"
            type="text"
            class="variable-input"
            :styles="{ marginBottom: 0 }"
          />
        </div>
      </template>

      <p v-if="hasInvalidValues" class="error mb-1">
        {{
          isMessageTooLong
            ? $t('MESSENGER_TEMPLATES.PARSER.MESSAGE_TOO_LONG_ERROR_MESSAGE')
            : $t('MESSENGER_TEMPLATES.PARSER.INVALID_PARAMETER_MESSAGE')
        }}
      </p>
      <p v-if="dirty && hasEmptyValues" class="error">
        {{ $t('MESSENGER_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>
    <footer>
      <woot-button variant="smooth" @click="$emit('resetTemplate')">
        {{ $t('MESSENGER_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </woot-button>
      <woot-button @click="sendMessage">
        {{ $t('MESSENGER_TEMPLATES.PARSER.SEND_MESSAGE_LABEL') }}
      </woot-button>
    </footer>
  </div>
</template>

<script>
import { isValidParameter } from 'shared/helpers/Validators';
import { TEMPLATE_MAX_BODY_LENGTH } from 'shared/constants/messages';
import { getTemplateMessageLength } from 'shared/helpers/TemplateHelper';
export default {
  props: {
    template: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      // Cada variable: { raw: '{{1}}', token: '1', value: '' }
      headerVars: [],
      bodyVars: [],
      // Variables de botones: { raw, token, value, index, type: 'URL'|'POSTBACK' }
      buttonVars: [],
      dirty: false,
    };
  },
  computed: {
    showHeader() {
      return false; // Por ahora no se mostrará el header
    },
    showButtons() {
      return false; // Por ahora no se mostrarán los botones
    },
    headerString() {
      const header = this.template.components.find(
        component => component.type === 'HEADER' && component.format === 'TEXT'
      );
      return (header && header.text) || '';
    },
    bodyString() {
      const body = this.template.components.find(
        component => component.type === 'BODY'
      );
      return (body && body.text) || '';
    },
    // Revisar si la plantilla tiene variables que llenar en header, body o botones para mostrar el formulario de completado de variables.
    hasVariables() {
      return (
        (this.headerVars.length > 0 && this.showHeader) ||
        this.bodyVars.length > 0 ||
        (this.buttonVars.length > 0 && this.showButtons)
      );
    },
    // Variables que el agente puede llenar: solo las secciones visibles participan en la validación.
    visibleVars() {
      const vars = [];
      if (this.showHeader) vars.push(...this.headerVars);
      vars.push(...this.bodyVars);
      if (this.showButtons) vars.push(...this.buttonVars);
      return vars;
    },
    // Valida que no haya variables sin completar antes de enviar el mensaje.
    hasEmptyValues() {
      return this.visibleVars.some(item => !item.value);
    },
    // Máximo permitido por Facebook para el body procesado (constante compartida, no estado).
    maxLength() {
      return TEMPLATE_MAX_BODY_LENGTH.FACEBOOK;
    },
    // Los placeholders sin llenar no suman: el contador inicia con el texto fijo y solo crece al
    // escribir (al enviar, con todas las variables llenas, coincide con processedString).
    messageLength() {
      const valuesByToken = this.bodyVars.reduce((acc, item) => {
        acc[item.token] = item.value;
        return acc;
      }, {});
      return getTemplateMessageLength(this.bodyString, valuesByToken);
    },
    // Indica si el cuerpo excede el tamaño máximo permitido por Facebook (mismo número que el
    // contador, para que el error y el rojo sean coherentes con lo mostrado).
    isMessageTooLong() {
      return this.messageLength > this.maxLength;
    },
    // Valida que ninguna variable contenga caracteres no permitidos (#, %, $ o emojis) y que el mensaje no exceda el tamaño máximo.
    hasInvalidValues() {
      return (
        this.visibleVars.some(item => !isValidParameter(item.value)) ||
        this.isMessageTooLong
      );
    },
    // Texto del cuerpo procesado, usado como contenido del mensaje en la conversación.
    processedString() {
      return this.replacePlaceholders(this.bodyString, this.bodyVars);
    },
    // Texto del header procesado (con las variables del header sustituidas en vivo),
    // para mostrar la vista previa de lo que vería el usuario en el encabezado.
    processedHeaderString() {
      return this.replacePlaceholders(this.headerString, this.headerVars);
    },
    // Botones definidos en la plantilla, para representar lo que vería el usuario.
    templateButtons() {
      const buttonsComponent = this.template.components.find(
        component => component.type === 'BUTTONS'
      );
      return (buttonsComponent && buttonsComponent.buttons) || [];
    },
  },
  mounted() {
    this.headerVars = this.buildVariableList(this.headerString);
    this.bodyVars = this.buildVariableList(this.bodyString);
    this.buttonVars = this.buildButtonVariableList();
  },
  methods: {
    sendMessage() {
      this.dirty = true;
      if (this.hasInvalidValues || (this.hasVariables && this.hasEmptyValues))
        return;
      // El header y los botones se llevan en content_attributes para representar la tarjeta
      // de Messenger en el chat (header en negrita arriba; botones como los del bot vía items).
      // El content se mantiene como solo el body.
      const contentAttributes = {};
      if (this.processedHeaderString) {
        contentAttributes.header = this.processedHeaderString;
      }
      if (this.templateButtons.length) {
        contentAttributes.items = this.templateButtons.map(button => ({
          title: button.text,
        }));
      }
      const payload = {
        message: this.processedString,
        contentAttributes: Object.keys(contentAttributes).length
          ? contentAttributes
          : undefined,
        templateParams: {
          name: this.template.name,
          category: this.template.category,
          language: this.template.language,
          namespace: this.template.namespace,
          processed_params: {
            // POSITIONAL o NAMED. Para NAMED el backend usa el token como parameter_name.
            parameter_format: this.template.parameter_format || 'POSITIONAL',
            header: this.headerVars.map(item => ({
              token: item.token,
              value: item.value,
            })),
            body: this.bodyVars.map(item => ({
              token: item.token,
              value: item.value,
            })),
            buttons: this.buttonVars.map(item => ({
              index: item.index,
              type: item.type,
              token: item.token,
              value: item.value,
            })),
          },
        },
      };
      this.$emit('sendMessage', payload);
    },
    processVariable(str) {
      return str.replace(/{{|}}/g, '');
    },
    // Extrae las variables de los botones dinámicos (URL con {{...}} o POSTBACK con
    // payload variable), preservando el índice del botón dentro del componente BUTTONS.
    // Los botones sin variable (estáticos) se ignoran.
    buildButtonVariableList() {
      const buttonsComponent = this.template.components.find(
        component => component.type === 'BUTTONS'
      );
      if (!buttonsComponent || !buttonsComponent.buttons) return [];

      return buttonsComponent.buttons.reduce((acc, button, index) => {
        const source = button.type === 'URL' ? button.url : button.payload;
        const match = (source || '').match(/{{([^}]+)}}/);
        if (match) {
          acc.push({
            raw: match[0],
            token: this.processVariable(match[0]),
            value: '',
            index,
            type: button.type,
          });
        }
        return acc;
      }, []);
    },
    // Construye la lista posicional de variables de un texto, deduplicando por token
    // y preservando el orden de aparición.
    buildVariableList(str) {
      const matched = str.match(/{{([^}]+)}}/g) || [];
      return matched.reduce((acc, raw) => {
        const token = this.processVariable(raw);
        if (!acc.some(item => item.token === token)) {
          acc.push({ raw, token, value: '' });
        }
        return acc;
      }, []);
    },
    replacePlaceholders(str, vars) {
      return str.replace(/{{([^}]+)}}/g, (match, variable) => {
        const token = this.processVariable(variable);
        const found = vars.find(item => item.token === token);
        return (found && found.value) || `{{${variable}}}`;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.template__variables-container {
  padding: var(--space-one);
}

.variables-label {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-one);
}

.component-label {
  color: var(--s-500);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-smaller);
  text-transform: uppercase;
}

.template__variable-item {
  align-items: center;
  display: flex;
  margin-bottom: var(--space-one);

  .label {
    font-size: var(--font-size-mini);
  }

  .variable-input {
    flex: 1;
    font-size: var(--font-size-small);
    margin-left: var(--space-one);
  }

  .variable-label {
    background-color: var(--s-75);
    border-radius: var(--border-radius-normal);
    display: inline-block;
    font-size: var(--font-size-mini);
    padding: var(--space-one) var(--space-medium);
  }
}

footer {
  display: flex;
  justify-content: flex-end;

  button {
    margin-left: var(--space-one);
  }
}
.error {
  background-color: var(--r-100);
  border-radius: var(--border-radius-normal);
  color: var(--r-800);
  padding: var(--space-one);
  text-align: center;

  &.mb-1 {
    margin-bottom: var(--space-one);
  }
}
.template-input {
  background-color: var(--s-25);
  margin-bottom: var(--space-zero);
}

.template-input--error {
  border-color: var(--r-500);
}

.template-preview__header {
  color: var(--s-800);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-smaller);
}

.template-preview__buttons {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  margin-top: var(--space-small);
}

.template-preview__button {
  border: 1px solid var(--w-300);
  border-radius: var(--border-radius-normal);
  color: var(--w-600);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  padding: var(--space-small);
  text-align: center;
}

.size-indicator-container {
  color: var(--conversa2-neutral-800-color);
  display: flex;
  font-size: var(--font-size-mini);
  justify-content: flex-end;
  margin-top: var(--space-zero);
}

.size-indicator-container--error {
  color: var(--r-500);
}
</style>
